import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useActivityLogger, ACTIVITY_TYPES } from "./useActivityLogger";
import { logger } from "@/lib/logger";

const SESSION_CHECK_INTERVAL = 120000; // Check every 2 minutes (reduced frequency)
const INACTIVITY_WARNING_TIME = 25 * 60 * 1000; // 25 minutes
const INACTIVITY_LOGOUT_TIME = 30 * 60 * 1000; // 30 minutes
const SESSION_CHECK_DEBOUNCE = 10000; // Debounce session checks by 10 seconds

export function useSessionMonitor(isAdminRoute: boolean = false) {
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [showWarning, setShowWarning] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { logActivity } = useActivityLogger();
  const hasLoggedLogin = useRef(false);
  const lastSessionCheck = useRef(0);
  const sessionCheckTimeout = useRef<NodeJS.Timeout>();

  // Track user activity
  const lastActivityRef = useRef(Date.now());
  const updateActivity = useCallback(async () => {
    lastActivityRef.current = Date.now();
    setLastActivity(Date.now());
    setShowWarning(false);

    // Update active_sessions table with error handling (silently fail to avoid blocking UI)
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      // Silently update session - don't block if it fails
      supabase
        .from("active_sessions")
        .upsert({
          user_id: session.user.id,
          session_token: session.access_token,
          last_activity: new Date().toISOString(),
          is_active: true,
        }, {
          onConflict: "session_token",
        });
    } catch (error) {
      // Silently fail to prevent UI blocking - session updates are non-critical
      logger.debug("Session activity update skipped", error);
    }
  }, []);

  // Check session validity with debouncing
  const checkSession = useCallback(async () => {
    const now = Date.now();
    
    // Debounce session checks to prevent rapid fire requests
    if (now - lastSessionCheck.current < SESSION_CHECK_DEBOUNCE) {
      return true;
    }
    
    lastSessionCheck.current = now;

    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      // Only redirect on actual auth errors, not network issues
      if (error?.message?.includes('Failed to fetch')) {
        logger.warn('Network error during session check');
        return true; // Assume session is valid during network issues
      }
      
      if (error || !session) {
        if (isAdminRoute) {
          logger.info('Session expired, redirecting to auth');
          toast({
            title: "Session Expired",
            description: "Your session has expired. Please log in again.",
            variant: "destructive",
          });
          navigate("/auth");
        }
        return false;
      }

      const inactiveTime = Date.now() - lastActivity;

      // Show warning before logout
      if (inactiveTime > INACTIVITY_WARNING_TIME && inactiveTime < INACTIVITY_LOGOUT_TIME) {
        if (!showWarning) {
          setShowWarning(true);
          toast({
            title: "Inactivity Warning",
            description: "Your session will expire in 5 minutes due to inactivity.",
          });
        }
      }

      // Auto logout after inactivity
      if (inactiveTime > INACTIVITY_LOGOUT_TIME) {
        await supabase.auth.signOut();
        toast({
          title: "Session Expired",
          description: "You were logged out due to inactivity.",
          variant: "destructive",
        });
        navigate("/auth");
        return false;
      }

      return true;
    } catch (error) {
      logger.error("Session check error", error);
      // Don't redirect on errors, could be temporary network issue
      return true;
    }
  }, [lastActivity, showWarning, isAdminRoute, toast, navigate]);

  // Log admin session - only once per session
  const logAdminSession = useCallback(async (action: "login" | "logout") => {
    if (action === "login" && hasLoggedLogin.current) {
      return; // Already logged this session
    }
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;

      // Use the enhanced activity logger with IP, device, and geolocation
      await logActivity(
        action === "login" ? ACTIVITY_TYPES.ADMIN_LOGIN : ACTIVITY_TYPES.ADMIN_LOGOUT,
        {
          action: action === "login" ? "Admin logged in" : "Admin logged out",
          target_type: "session",
        }
      );
      
      if (action === "login") {
        hasLoggedLogin.current = true;
      }
    } catch (error) {
      logger.error("Failed to log admin session", error);
    }
  }, [logActivity]);

  useEffect(() => {
    if (!isAdminRoute) return;

    // Wait for geolocation/device data to load before logging
    const loginTimeout = setTimeout(async () => {
      // Keep trying to get IP/geolocation data with retries
      let retries = 0;
      const maxRetries = 5;
      
      const logWithRetry = async () => {
        try {
          await logAdminSession("login");
        } catch (error) {
          logger.error(`Admin session log failed (attempt ${retries + 1})`, error);
          if (retries < maxRetries) {
            retries++;
            setTimeout(logWithRetry, 2000); // Retry after 2 seconds
          }
        }
      };
      
      await logWithRetry();
    }, 5000); // 5 second delay to ensure IP/device data is fully loaded

    // Track user activity
    const activityEvents = ["mousedown", "keydown", "scroll", "touchstart"];
    activityEvents.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    // Check session periodically
    const intervalId = setInterval(checkSession, SESSION_CHECK_INTERVAL);

    // Cleanup
    return () => {
      clearTimeout(loginTimeout);
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      clearInterval(intervalId);
      if (sessionCheckTimeout.current) {
        clearTimeout(sessionCheckTimeout.current);
      }
    };
  }, [isAdminRoute, updateActivity, checkSession, logAdminSession]);

  return {
    lastActivity,
    showWarning,
    updateActivity,
    logAdminSession,
  };
}
